import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  getHospitalSubscription, 
  getSubscriptionPlans, 
  getPaymentHistory,
  getDaysRemaining,
  type HospitalSubscription,
  type SubscriptionPlan,
  type PaymentHistory
} from '@/lib/subscription';
import { CreditCard, Calendar, CheckCircle, Clock, XCircle, AlertCircle, Download } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { PaymentDialog } from '@/components/payment/PaymentDialog';
import { useHospitalProfileStore } from '@/lib/stores/hospitalProfile';

export function SubscriptionSettings() {
  const { toast } = useToast();
  const { profile } = useHospitalProfileStore();
  const [subscription, setSubscription] = useState<HospitalSubscription | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedPlanForPayment, setSelectedPlanForPayment] = useState<SubscriptionPlan | null>(null);

  useEffect(() => {
    loadSubscriptionData();
    // Load hospital profile for receipt generation
    const { fetchProfile } = useHospitalProfileStore.getState();
    fetchProfile();
  }, []);

  const loadSubscriptionData = async () => {
    setLoading(true);
    try {
      const [subData, plansData, historyData] = await Promise.all([
        getHospitalSubscription(),
        getSubscriptionPlans(),
        getPaymentHistory()
      ]);
      setSubscription(subData);
      setPlans(plansData);
      setPaymentHistory(historyData);
    } catch (error) {
      console.error('Error loading subscription data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load subscription information',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const { daysRemaining, isTrial, isExpired } = getDaysRemaining(subscription);

  const getStatusBadge = () => {
    if (!subscription) return null;

    const status = subscription.subscription_status;
    const variants: Record<string, any> = {
      trial: { variant: 'outline' as const, className: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: Clock },
      active: { variant: 'outline' as const, className: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle },
      expired: { variant: 'outline' as const, className: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
      suspended: { variant: 'outline' as const, className: 'bg-orange-50 text-orange-700 border-orange-200', icon: AlertCircle },
      cancelled: { variant: 'outline' as const, className: 'bg-gray-50 text-gray-700 border-gray-200', icon: XCircle }
    };

    const config = variants[status] || variants.expired;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const handleSelectPlan = (planId: string) => {
    const plan = plans.find(p => p.id === planId);
    if (plan) {
    setSelectedPlan(planId);
      setSelectedPlanForPayment(plan);
      setPaymentDialogOpen(true);
    } else {
    toast({
        title: 'Error',
        description: 'Selected plan not found.',
        variant: 'destructive',
    });
    }
  };

  const handleRenewSubscription = () => {
    if (subscription?.subscription_plan_id) {
      handleSelectPlan(subscription.subscription_plan_id);
    } else {
      // If no current plan, let user select a new plan
      toast({
        title: 'Select a Plan',
        description: 'Please select a subscription plan from below.',
      });
    }
  };

  const handlePaymentSuccess = async () => {
    // Reload subscription data after successful payment
    await loadSubscriptionData();
    setPaymentDialogOpen(false);
    setSelectedPlanForPayment(null);
  };

  const handleDownloadReceipt = (payment: PaymentHistory) => {
    // Get plan name
    const plan = plans.find(p => p.id === payment.subscription_plan_id);
    const planName = plan?.name || 'Subscription Plan';
    
    // Create a new window for printing/downloading
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast({
        title: 'Error',
        description: 'Please allow popups to download the receipt',
        variant: 'destructive'
      });
      return;
    }

    // Create the receipt HTML
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Payment Receipt - ${payment.transaction_id || payment.id}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px 40px;
              font-size: 12px;
              line-height: 1.6;
              color: #000;
              background: white;
            }
            
            .receipt-header {
              text-align: center;
              margin-bottom: 30px;
              padding-bottom: 15px;
              border-bottom: 2px solid #000;
            }
            
            .clinic-name {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 8px;
              color: #1e40af;
            }
            
            .clinic-details {
              font-size: 11px;
              color: #666;
              margin-bottom: 4px;
            }
            
            .receipt-title {
              font-size: 20px;
              font-weight: bold;
              margin-top: 20px;
              margin-bottom: 10px;
            }
            
            .receipt-info {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 20px;
              margin-bottom: 30px;
              font-size: 11px;
            }
            
            .info-section {
              display: flex;
              flex-direction: column;
              gap: 8px;
            }
            
            .info-row {
              display: flex;
              justify-content: space-between;
              padding: 4px 0;
            }
            
            .info-label {
              font-weight: bold;
              color: #555;
            }
            
            .info-value {
              color: #000;
            }
            
            .payment-details {
              margin-bottom: 30px;
              border: 1px solid #ddd;
              border-radius: 4px;
              overflow: hidden;
            }
            
            .payment-details-header {
              background: #f3f4f6;
              padding: 12px;
              font-weight: bold;
              border-bottom: 1px solid #ddd;
            }
            
            .payment-details-body {
              padding: 12px;
            }
            
            .payment-row {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
              border-bottom: 1px solid #eee;
            }
            
            .payment-row:last-child {
              border-bottom: none;
            }
            
            .payment-label {
              color: #666;
            }
            
            .payment-value {
              font-weight: 600;
              color: #000;
            }
            
            .total-section {
              margin-top: 20px;
              padding-top: 15px;
              border-top: 2px solid #000;
            }
            
            .total-row {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
              font-size: 13px;
            }
            
            .total-row.final {
              font-size: 16px;
              font-weight: bold;
              color: #1e40af;
            }
            
            .status-badge {
              display: inline-block;
              padding: 4px 12px;
              border-radius: 4px;
              font-size: 11px;
              font-weight: bold;
              text-transform: uppercase;
            }
            
            .status-completed {
              background: #d1fae5;
              color: #065f46;
            }
            
            .status-pending {
              background: #fef3c7;
              color: #92400e;
            }
            
            .status-failed {
              background: #fee2e2;
              color: #991b1b;
            }
            
            .status-refunded {
              background: #e0e7ff;
              color: #3730a3;
            }
            
            .footer {
              text-align: center;
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
              font-size: 10px;
              color: #666;
            }
            
            .notes {
              margin-top: 20px;
              padding: 12px;
              background: #f9fafb;
              border-left: 3px solid #3b82f6;
              font-size: 11px;
              color: #555;
            }
            
            @media print {
              body {
                padding: 10px 20px;
              }
            }
          </style>
        </head>
        <body>
          <div class="receipt-header">
            <div class="receipt-title">PAYMENT RECEIPT</div>
          </div>
          
          <div class="receipt-info">
            <div class="info-section">
              <div class="info-row">
                <span class="info-label">Receipt Number:</span>
                <span class="info-value">${payment.transaction_id || payment.id.substring(0, 8).toUpperCase()}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Payment Date:</span>
                <span class="info-value">${format(new Date(payment.payment_date), 'dd MMM yyyy, hh:mm a')}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Payment Method:</span>
                <span class="info-value">${payment.payment_method.charAt(0).toUpperCase() + payment.payment_method.slice(1)}</span>
              </div>
            </div>
            <div class="info-section">
              <div class="info-row">
                <span class="info-label">Status:</span>
                <span class="info-value">
                  <span class="status-badge status-${payment.status}">${payment.status}</span>
                </span>
              </div>
              <div class="info-row">
                <span class="info-label">Subscription Period:</span>
                <span class="info-value">${payment.subscription_period_months} months</span>
              </div>
              <div class="info-row">
                <span class="info-label">Plan:</span>
                <span class="info-value">${planName}</span>
              </div>
            </div>
          </div>
          
          <div class="payment-details">
            <div class="payment-details-header">Payment Details</div>
            <div class="payment-details-body">
              <div class="payment-row">
                <span class="payment-label">Subscription Plan</span>
                <span class="payment-value">${planName}</span>
              </div>
              <div class="payment-row">
                <span class="payment-label">Duration</span>
                <span class="payment-value">${payment.subscription_period_months} months</span>
              </div>
              <div class="payment-row">
                <span class="payment-label">Payment Method</span>
                <span class="payment-value">${payment.payment_method.charAt(0).toUpperCase() + payment.payment_method.slice(1)}</span>
              </div>
              ${payment.transaction_id ? `
              <div class="payment-row">
                <span class="payment-label">Transaction ID</span>
                <span class="payment-value">${payment.transaction_id}</span>
              </div>
              ` : ''}
            </div>
          </div>
          
          <div class="total-section">
            ${payment.base_amount && payment.tax_amount ? `
            <div class="total-row">
              <span>Base Amount</span>
              <span>${payment.currency} ${payment.base_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div class="total-row">
              <span>GST (${payment.tax_rate ? payment.tax_rate.toFixed(2) : '18.00'}%)</span>
              <span>${payment.currency} ${payment.tax_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            ` : `
            <div class="total-row">
              <span>Subtotal</span>
              <span>${payment.currency} ${payment.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            `}
            <div class="total-row final">
              <span>Total Amount Paid</span>
              <span>${payment.currency} ${payment.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          </div>
          
          ${payment.notes ? `
          <div class="notes">
            <strong>Notes:</strong> ${payment.notes}
          </div>
          ` : ''}
          
          <div class="footer">
            <p><strong>Thank you for your subscription!</strong></p>
            <p>This is a computer-generated receipt. No signature required.</p>
            <p>Generated on ${format(new Date(), 'dd MMM yyyy, hh:mm a')}</p>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    
    // Wait for content to load then print
    printWindow.onload = () => {
      printWindow.print();
      // Optionally close after printing (user can also save as PDF)
      // printWindow.close();
    };
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="pt-6">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Subscription Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Current Subscription</CardTitle>
              <CardDescription>
                Manage your subscription plan and billing
              </CardDescription>
            </div>
            {getStatusBadge()}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {subscription ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Hospital Name</p>
                  <p className="text-lg font-semibold">{subscription.name}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Status</p>
                  <p className="text-lg">{subscription.subscription_status.charAt(0).toUpperCase() + subscription.subscription_status.slice(1)}</p>
                </div>
                {subscription.subscription_expires_at && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">
                      {isTrial ? 'Trial Expires' : 'Subscription Expires'}
                    </p>
                    <p className="text-lg font-semibold">
                      {format(new Date(subscription.subscription_expires_at), 'PPP')}
                    </p>
                  </div>
                )}
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Time Remaining</p>
                  <p className="text-lg font-semibold">
                    {daysRemaining > 0 ? `${daysRemaining} days` : 'Expired'}
                  </p>
                </div>
                {subscription.last_payment_date && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Last Payment</p>
                    <p className="text-lg">
                      {format(new Date(subscription.last_payment_date), 'PPP')}
                    </p>
                  </div>
                )}
                {subscription.next_payment_date && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Next Payment</p>
                    <p className="text-lg">
                      {format(new Date(subscription.next_payment_date), 'PPP')}
                    </p>
                  </div>
                )}
              </div>
              {(isExpired || daysRemaining < 7) && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-yellow-900">
                        {isExpired ? 'Subscription Expired' : 'Renewal Due Soon'}
                      </p>
                      <p className="text-sm text-yellow-700 mt-1">
                        {isExpired 
                          ? 'Your subscription has expired. Please renew to continue using the service.'
                          : `Your subscription expires in ${daysRemaining} days. Renew now to avoid service interruption.`
                        }
                      </p>
                      <Button 
                        className="mt-3" 
                        onClick={handleRenewSubscription}
                      >
                        {isExpired ? 'Renew Subscription' : 'Renew Now'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No subscription found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Available Plans */}
      <Card>
        <CardHeader>
          <CardTitle>Subscription Plans</CardTitle>
          <CardDescription>
            Choose a plan that fits your hospital's needs
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* All Plans Include Section */}
          <div className="bg-gray-50 p-4 rounded-lg border">
            <h3 className="font-semibold mb-3 text-lg">All Plans Include</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span>Unlimited Users</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span>Unlimited Patients</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span>Patient Registration & Management</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span>WhatsApp Integration & Prescription PDF Share</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span>Real-time Queue System</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span>E-Prescription Generation</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span>Reports & Analytics</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span>Doctor & Staff Management</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span>Patient History Tracking</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span>Medication Templates</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span>Follow-up Management</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span>Revenue Tracking</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span>Print Prescriptions</span>
              </div>
            </div>
          </div>

          {/* Plan Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((plan) => {
              const features = plan.features || {};
              const isSelected = selectedPlan === plan.id;
              const isCurrentPlan = subscription?.subscription_plan_id === plan.id;

              // Calculate savings compared to 3-month plan
              // Find the 3-month plan to use as base for discount calculation
              const base3MonthPlan = plans.find(p => p.duration_months === 3);
              let savings = 0;
              let discountPercent = 0;
              
              if (base3MonthPlan && plan.duration_months > 3) {
                // Calculate what it would cost to pay 3-month plan multiple times
                const equivalentPrice = (base3MonthPlan.price / 3) * plan.duration_months;
                savings = equivalentPrice - plan.price;
                discountPercent = Math.round((savings / equivalentPrice) * 100);
              }
              const isBestValue = plan.duration_months === 12;
              const isPopular = plan.duration_months === 6;

              return (
                <Card 
                  key={plan.id} 
                  className={`relative ${isSelected ? 'ring-2 ring-hospital-primary' : ''} ${isCurrentPlan ? 'bg-hospital-primary/5' : ''} ${isBestValue ? 'border-green-500 border-2 bg-green-50/30' : ''} ${isPopular ? 'border-blue-500 border-2' : ''}`}
                >
                  <div className="absolute top-2 right-2 flex flex-col gap-1">
                    {isCurrentPlan && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                        Current Plan
                      </Badge>
                    )}
                    {isBestValue && !isCurrentPlan && (
                      <Badge className="bg-green-100 text-green-700 border-green-300">
                        Best Value
                      </Badge>
                    )}
                    {isPopular && !isCurrentPlan && !isBestValue && (
                      <Badge className="bg-blue-100 text-blue-700 border-blue-300">
                        Popular
                      </Badge>
                    )}
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl">{plan.name}</CardTitle>
                    <div className="mt-2">
                      <span className="text-3xl font-bold">₹{plan.price.toLocaleString('en-IN')}</span>
                      <span className="text-muted-foreground"> / {plan.duration_months} months</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      ₹{Math.round(plan.price / plan.duration_months).toLocaleString('en-IN')}/month
                    </p>
                    {savings > 0 && (
                      <div className="mt-2">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-xs">
                          Save ₹{savings.toLocaleString('en-IN')} ({discountPercent}% off)
                        </Badge>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Button
                      className={`w-full ${isBestValue ? 'bg-green-600 hover:bg-green-700' : ''}`}
                      variant={isCurrentPlan ? 'outline' : isBestValue ? 'default' : 'default'}
                      onClick={() => handleSelectPlan(plan.id)}
                      disabled={isCurrentPlan}
                    >
                      {isCurrentPlan ? 'Current Plan' : isBestValue ? 'Choose Best Value' : 'Select Plan'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Payment History */}
      <Card>
        <CardHeader>
          <CardTitle>Payment History</CardTitle>
          <CardDescription>
            View all your subscription payments
          </CardDescription>
        </CardHeader>
        <CardContent>
          {paymentHistory.length > 0 ? (
            <div className="space-y-4">
              {paymentHistory.map((payment) => (
                <div
                  key={payment.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-hospital-primary/10 rounded-lg">
                      <CreditCard className="h-5 w-5 text-hospital-primary" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {payment.amount.toLocaleString('en-IN')} {payment.currency}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(payment.payment_date), 'PPP')} • {payment.payment_method}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge
                      variant={
                        payment.status === 'completed' ? 'default' :
                        payment.status === 'pending' ? 'secondary' :
                        'destructive'
                      }
                    >
                      {payment.status}
                    </Badge>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDownloadReceipt(payment)}
                      title="Download Receipt"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No payment history found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Dialog */}
      {selectedPlanForPayment && (
        <PaymentDialog
          open={paymentDialogOpen}
          onOpenChange={setPaymentDialogOpen}
          planId={selectedPlanForPayment.id}
          planName={selectedPlanForPayment.name}
          amount={selectedPlanForPayment.price}
          currency={selectedPlanForPayment.currency || 'INR'}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
}

